"use client";
import React, {
  ForwardedRef,
  forwardRef,
  ReactNode,
  TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from "react";

import clsx from "clsx";
import { useLocale } from "next-intl";

import {
  getDefaultLocale,
  getFilteredLanguageTabs,
  getSupportedLocalesArray,
  isSupportedLocale,
} from "@/config/locale.config";
import { SupportedLocale } from "@/enums/locale.enum";
import BaseTextarea from "@/ui/Forms/BaseTextarea";
import FormWrapper from "@/ui/Forms/FormWrapper";

import {
  MultiLingualErrors,
  MultiLingualValue,
  SupportedLanguage,
} from "../FormMultiLingualInput";

// onBlur handler type for textarea - fixed type issue
export type OnBlurHandler = (
  lang: string,
) => (event: React.FocusEvent<HTMLTextAreaElement>) => void;

export interface FormMultiLingualTextareaProps
  extends Omit<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    "onChange" | "value" | "required" | "onBlur"
  > {
  value: MultiLingualValue;
  onChange?: (value: MultiLingualValue) => void;
  label?: string;
  error?: MultiLingualErrors | string;
  labelRightComponent?: ReactNode;
  required?: boolean;
  languages?: SupportedLanguage[];
  mode?: "edit" | "readonly";
  synchronizedLanguage?: SupportedLanguage;
  onLanguageChange?: (language: SupportedLanguage) => void;
  errorClassName?: string;
  onBlur?: OnBlurHandler;
  fieldName?: string;
  clearErrors?: (fieldPath: string) => void;
}

/**
 * Multi-lingual textarea component for entering text in multiple languages
 */
const FormMultiLingualTextarea = (
  {
    value,
    onChange,
    label,
    error,
    labelRightComponent,
    required = false,
    languages = getSupportedLocalesArray(),
    mode = "edit",
    synchronizedLanguage,
    onLanguageChange,
    className,
    errorClassName,
    onBlur,
    fieldName,
    clearErrors,
    ...textareaProps
  }: FormMultiLingualTextareaProps,
  ref: ForwardedRef<HTMLTextAreaElement>,
) => {
  const [languageErrors, setLanguageErrors] = useState<Record<string, string>>(
    {},
  );
  const locale = useLocale();
  const currentLocale = isSupportedLocale(locale) ? locale : getDefaultLocale();
  const [activeLanguage, setActiveLanguage] = useState<SupportedLanguage>(
    synchronizedLanguage || currentLocale,
  );

  const getLanguageErrors = useCallback(
    (
      rawError: MultiLingualErrors | undefined | string,
      currentFieldName: string,
    ): Record<string, string> => {
      if (!rawError) {
        return {};
      }
      if (typeof rawError === "string") {
        return { [activeLanguage]: rawError };
      }
      const errors: Record<string, string> = {};
      const pattern = new RegExp(`^${currentFieldName}\\.(\\w+)$`);
      Object.entries(rawError).forEach(([key, value]) => {
        const match = key.match(pattern);
        if (match && value && typeof value === "object" && "message" in value) {
          const lang = match[1];
          if (typeof value.message === "string") {
            errors[lang] = value.message;
          }
        }
      });
      return errors;
    },
    [activeLanguage],
  );

  useEffect(() => {
    if (fieldName) {
      setLanguageErrors(getLanguageErrors(error, fieldName));
    }
  }, [error]);

  // Update active language if synchronizedLanguage changed
  useEffect(() => {
    if (synchronizedLanguage) {
      // Use dynamic supported languages from country config
      const validLanguages = getSupportedLocalesArray();
      const safeLanguage = validLanguages.includes(
        synchronizedLanguage as SupportedLocale,
      )
        ? (synchronizedLanguage as SupportedLocale)
        : validLanguages[0] || SupportedLocale.EL; // Fallback to first supported or EL

      if (languages.includes(safeLanguage)) {
        setActiveLanguage(safeLanguage);
      }
    }
  }, [synchronizedLanguage, languages]);

  // Get filtered language tabs based on available languages
  const availableTabs = getFilteredLanguageTabs(languages);

  const handleLanguageChange = (language: SupportedLanguage) => {
    setActiveLanguage(language);
    // Call callback for language synchronization with other components
    if (onLanguageChange) {
      onLanguageChange(language);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const currentValues = value || {};
    const newValue = { ...currentValues, [activeLanguage]: e.target.value };
    if (onChange) {
      onChange(newValue);
    }

    // If there was an error for this language, clear it from local state for immediate UI feedback
    if (languageErrors[activeLanguage]) {
      setLanguageErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[activeLanguage];
        return newErrors;
      });
    }

    // Also attempt to clear it in react-hook-form
    if (clearErrors) {
      clearErrors(`${fieldName}.${activeLanguage}`);
    }
  };

  const handleTextareaBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (onBlur) {
        onBlur(activeLanguage)(e);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [activeLanguage, onBlur],
  );

  // Standard editing mode
  return (
    <FormWrapper
      label={label}
      error={languageErrors[activeLanguage]}
      required={required}
      labelRight={labelRightComponent}
      errorClassName={errorClassName}
    >
      <div className="w-full relative">
        {/* Language tabs */}
        <div className="flex mb-[1rem] justify-end absolute top-[-3.6rem] z-[1] right-0">
          {availableTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={clsx(
                "py-[0.8rem] px-[1.5rem] bg-none text-[1.4rem] font-medium text-gray-1300 cursor-pointer",
                "transition-all duration-300 ease-in-out border-b-2 border-transparent border-solid",
                activeLanguage === tab.key
                  ? "text-red border-b-2 border-b-red"
                  : "hover:text-input-text",
                languageErrors[tab.key] ? "text-red" : "",
              )}
              onClick={() => handleLanguageChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Textarea field */}
        <BaseTextarea
          ref={ref}
          value={value[activeLanguage] || ""}
          onChange={handleTextareaChange}
          error={languageErrors[activeLanguage]}
          disabled={mode === "readonly"}
          className={className}
          onBlur={handleTextareaBlur}
          {...(textareaProps as any)}
        />
      </div>
    </FormWrapper>
  );
};

export default forwardRef(FormMultiLingualTextarea);
